import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Clock,
  BookOpen,
  User,
  Users,
  Plus,
  X,
  Edit,
  Trash2,
  RefreshCw,
  Save,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";

const ClassSchedulePage = () => {
  const [selectedDay, setSelectedDay] = useState("Monday");
  const [batches, setBatches] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  // Load schedules from localStorage
  const [schedules, setSchedules] = useState(() => {
    const saved = localStorage.getItem("classSchedules");
    return saved ? JSON.parse(saved) : [];
  });

  const [formData, setFormData] = useState({
    day: "Monday",
    startTime: "09:00",
    endTime: "10:30",
    subjectId: "",
    teacherId: "",
    batchId: "",
    room: "",
    topic: "",
  });

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Save schedules to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("classSchedules", JSON.stringify(schedules));
  }, [schedules]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [batchRes, teacherRes, subjectRes] = await Promise.all([
        axios.get(`${server}/batch/all`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        axios.get(`${server}/teacher/all`, {
          params: { pageSize: 100 },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        axios.get(`${server}/subject/all`, {
          params: { pageSize: 100 },
        }),
      ]);

      setBatches(batchRes.data.data || []);
      setTeachers(teacherRes.data.data || []);
      setSubjects(subjectRes.data.data || []);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      day: selectedDay,
      startTime: "09:00",
      endTime: "10:30",
      subjectId: "",
      teacherId: "",
      batchId: "",
      room: "",
      topic: "",
    });
    setEditingSchedule(null);
  };

  const handleOpenModal = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setFormData({
        day: schedule.day,
        startTime: schedule.startTime,
        endTime: schedule.endTime,
        subjectId: schedule.subjectId,
        teacherId: schedule.teacherId,
        batchId: schedule.batchId,
        room: schedule.room || "",
        topic: schedule.topic || "",
      });
    } else {
      resetForm();
      setFormData((prev) => ({ ...prev, day: selectedDay }));
    }
    setShowModal(true);
  };

  const handleSaveSchedule = () => {
    if (!formData.subjectId || !formData.teacherId || !formData.batchId) {
      toast.error("Please fill all required fields");
      return;
    }

    const subject = subjects.find((s) => s.id == formData.subjectId);
    const teacher = teachers.find((t) => t.id == formData.teacherId);
    const batch = batches.find((b) => b.id == formData.batchId);

    const scheduleEntry = {
      id: editingSchedule ? editingSchedule.id : Date.now(),
      day: formData.day,
      startTime: formData.startTime,
      endTime: formData.endTime,
      subjectId: formData.subjectId,
      subjectName: subject?.subject || "Unknown",
      teacherId: formData.teacherId,
      teacherName: teacher?.name || "Unknown",
      batchId: formData.batchId,
      batchName: batch?.batchName || "Unknown",
      room: formData.room,
      topic: formData.topic,
    };

    if (editingSchedule) {
      setSchedules((prev) =>
        prev.map((s) => (s.id === editingSchedule.id ? scheduleEntry : s))
      );
      toast.success("Schedule updated!");
    } else {
      setSchedules((prev) => [...prev, scheduleEntry]);
      toast.success("Schedule added!");
    }

    setShowModal(false);
    resetForm();
  };

  const handleDeleteSchedule = (id) => {
    if (window.confirm("Are you sure you want to delete this class?")) {
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      toast.success("Schedule deleted!");
    }
  };

  const getScheduleForDay = (day) => {
    return schedules
      .filter((s) => s.day === day)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <motion.div
        className="max-w-7xl mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              Class Schedule
            </h1>
            <p className="text-gray-400 text-lg">
              Manage weekly class timetable
            </p>
          </div>

          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-indigo-500/25 transition-all font-semibold"
          >
            <Plus className="w-5 h-5" />
            Add Class
          </button>
        </div>

        {/* Day Selection */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {days.map((day) => (
            <button
              key={day}
              onClick={() => setSelectedDay(day)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap ${
                selectedDay === day
                  ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/30"
                  : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white"
              }`}
            >
              {day}
              <span className="ml-2 text-xs opacity-70">
                ({getScheduleForDay(day).length})
              </span>
            </button>
          ))}
        </div>

        {/* Schedule List */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-white mb-4">
            {selectedDay}'s Classes
          </h2>

          {loading ? (
            <div className="flex justify-center py-20">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          ) : getScheduleForDay(selectedDay).length > 0 ? (
            <AnimatePresence>
              {getScheduleForDay(selectedDay).map((cls) => (
                <motion.div
                  key={cls.id}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                  className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-indigo-500 transition-all group"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    {/* Time & Subject */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <div className="px-3 py-1 bg-indigo-500/20 text-indigo-400 rounded-lg text-sm font-semibold flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {formatTime(cls.startTime)} -{" "}
                          {formatTime(cls.endTime)}
                        </div>
                        {cls.room && (
                          <div className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-sm font-semibold">
                            {cls.room}
                          </div>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-white mb-1">
                        {cls.subjectName}
                      </h3>
                      {cls.topic && (
                        <p className="text-gray-400">{cls.topic}</p>
                      )}
                    </div>

                    {/* Batch & Teacher */}
                    <div className="flex flex-col gap-2 min-w-[200px]">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Users className="w-4 h-4 text-orange-400" />
                        <span className="text-sm">{cls.batchName}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <User className="w-4 h-4 text-green-400" />
                        <span className="text-sm">{cls.teacherName}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleOpenModal(cls)}
                        className="p-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSchedule(cls.id)}
                        className="p-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          ) : (
            <div className="text-center py-20 bg-gray-800/50 rounded-2xl border border-dashed border-gray-700">
              <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                No classes scheduled for {selectedDay}
              </p>
              <button
                onClick={() => handleOpenModal()}
                className="mt-4 text-indigo-400 hover:text-indigo-300 font-medium"
              >
                Schedule a Class
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-gray-800 rounded-2xl p-6 max-w-lg w-full border border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingSchedule ? "Edit Class" : "Add New Class"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Day *
                    </label>
                    <select
                      value={formData.day}
                      onChange={(e) =>
                        setFormData({ ...formData, day: e.target.value })
                      }
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    >
                      {days.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Room
                    </label>
                    <input
                      type="text"
                      value={formData.room}
                      onChange={(e) =>
                        setFormData({ ...formData, room: e.target.value })
                      }
                      placeholder="e.g., Room 101"
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Start Time *
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) =>
                        setFormData({ ...formData, startTime: e.target.value })
                      }
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      End Time *
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) =>
                        setFormData({ ...formData, endTime: e.target.value })
                      }
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Batch *
                  </label>
                  <select
                    value={formData.batchId}
                    onChange={(e) =>
                      setFormData({ ...formData, batchId: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Select Batch</option>
                    {batches.map((batch) => (
                      <option key={batch.id} value={batch.id}>
                        {batch.batchName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Subject *
                  </label>
                  <select
                    value={formData.subjectId}
                    onChange={(e) =>
                      setFormData({ ...formData, subjectId: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.subject}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Teacher *
                  </label>
                  <select
                    value={formData.teacherId}
                    onChange={(e) =>
                      setFormData({ ...formData, teacherId: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Select Teacher</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Topic (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.topic}
                    onChange={(e) =>
                      setFormData({ ...formData, topic: e.target.value })
                    }
                    placeholder="e.g., Introduction to Calculus"
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSchedule}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {editingSchedule ? "Update" : "Save"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClassSchedulePage;
